import { useState, useCallback, useRef } from 'react';
import { fixedAssetsApi } from '../services/api/endpoints/fixedAssets';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { sumMoney } from '../utils/currency';
import type {
  FixedAsset,
  CreateFixedAssetPayload,
  UpdateFixedAssetPayload,
  GenerateDepreciationPayload,
  DepreciationResult,
  DisposeFixedAssetPayload,
  ScrapFixedAssetPayload,
  FixedAssetLifecycleResult,
  FixedAssetTransaction,
} from '../types/fixed-assets';

export function useFixedAssets() {
  const [assets, setAssets] = useState<FixedAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentFiscalYear } = useFiscalYearStore();
  const loadRequestIdRef = useRef(0);

  const loadAssets = useCallback(async (params?: { status?: string; search?: string }) => {
    const requestId = ++loadRequestIdRef.current;
    try {
      setLoading(true);
      setError(null);

      const response = await fixedAssetsApi.getAll({
        fiscalYearId: currentFiscalYear?.id,
        ...params,
      });

      if (requestId !== loadRequestIdRef.current) return;

      if (response && 'data' in response) {
        const data = Array.isArray(response.data) ? response.data : (response.data as any)?.data || [];
        setAssets(data);
      }
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) return;
      setError(err.message || 'Erreur lors du chargement des immobilisations');
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [currentFiscalYear?.id]);

  const createAsset = useCallback(async (data: CreateFixedAssetPayload): Promise<FixedAsset | null> => {
    try {
      setError(null);
      const response = await fixedAssetsApi.create(data);
      if (response && 'data' in response) {
        const newAsset = (response.data as any)?.data || response.data;
        setAssets(prev => [newAsset as FixedAsset, ...prev]);
        return newAsset as FixedAsset;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la création');
      return null;
    }
  }, []);

  const updateAsset = useCallback(async (id: string, data: UpdateFixedAssetPayload): Promise<FixedAsset | null> => {
    try {
      setError(null);
      const response = await fixedAssetsApi.update(id, data);
      if (response && 'data' in response) {
        const updated = (response.data as any)?.data || response.data;
        setAssets(prev => prev.map(a => a.id === id ? updated as FixedAsset : a));
        return updated as FixedAsset;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour');
      return null;
    }
  }, []);

  const deleteAsset = useCallback(async (id: string): Promise<boolean> => {
    try {
      setError(null);
      await fixedAssetsApi.delete(id);
      setAssets(prev => prev.filter(a => a.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression');
      return false;
    }
  }, []);

  const generateDepreciation = useCallback(async (data: GenerateDepreciationPayload): Promise<DepreciationResult | null> => {
    try {
      setError(null);
      const response = await fixedAssetsApi.generateDepreciation(data);
      if (response && 'data' in response) {
        const result = (response.data as any)?.data || response.data;
        // Reload assets to reflect updated NBV and cumulative depreciation
        await loadAssets();
        return result as DepreciationResult;
      }
      return null;
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la génération des amortissements');
      return null;
    }
  }, [loadAssets]);

  const disposeAsset = useCallback(
    async (
      id: string,
      data: DisposeFixedAssetPayload,
    ): Promise<FixedAssetLifecycleResult | null> => {
      try {
        setError(null);
        const response = await fixedAssetsApi.dispose(id, data);
        if (response && 'data' in response) {
          const result = (response.data as any)?.data || response.data;
          if (result?.asset) {
            setAssets((prev) =>
              prev.map((a) => (a.id === id ? (result.asset as FixedAsset) : a)),
            );
          }
          return result as FixedAssetLifecycleResult;
        }
        return null;
      } catch (err: any) {
        setError(err.message || 'Erreur lors de la cession');
        return null;
      }
    },
    [],
  );

  const scrapAsset = useCallback(
    async (
      id: string,
      data: ScrapFixedAssetPayload,
    ): Promise<FixedAssetLifecycleResult | null> => {
      try {
        setError(null);
        const response = await fixedAssetsApi.scrap(id, data);
        if (response && 'data' in response) {
          const result = (response.data as any)?.data || response.data;
          if (result?.asset) {
            setAssets((prev) =>
              prev.map((a) => (a.id === id ? (result.asset as FixedAsset) : a)),
            );
          }
          return result as FixedAssetLifecycleResult;
        }
        return null;
      } catch (err: any) {
        setError(err.message || 'Erreur lors de la mise au rebut');
        return null;
      }
    },
    [],
  );

  const getTransactions = useCallback(
    async (id: string): Promise<FixedAssetTransaction[]> => {
      try {
        const response = await fixedAssetsApi.getTransactions(id);
        if (response && 'data' in response) {
          const list = (response.data as any)?.data || response.data;
          return Array.isArray(list) ? (list as FixedAssetTransaction[]) : [];
        }
        return [];
      } catch (err: any) {
        setError(err.message || "Erreur lors du chargement de l'historique");
        return [];
      }
    },
    [],
  );

  // Computed values
  const activeAssets = assets.filter(a => a.status === 'active');
  const totalNetBookValue = sumMoney(activeAssets.map(a => a.netBookValue));
  const totalAcquisitionCost = sumMoney(activeAssets.map(a => a.acquisitionCost));

  return {
    assets,
    loading,
    error,
    loadAssets,
    createAsset,
    updateAsset,
    deleteAsset,
    generateDepreciation,
    disposeAsset,
    scrapAsset,
    getTransactions,
    activeAssets,
    totalNetBookValue,
    totalAcquisitionCost,
  };
}
